import {type List} from 'immutable';

import createSelector from 'web-app/util/custom-create-selector';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import ChildBillPayersBehaviorIds from 'web-app/behaviors/child-bill-payers-behavior-ids';
import ChildBehaviors from 'web-app/behaviors/child-behavior-ids';
import {type RootState} from 'web-app/react/redux/main-reducer';

import ChildrenEntity from './index';

export const getChildren = (state: RootState) => state.entities.children;

export const makeGetChildByProps = () => {
    return createSelector(
        getChildren,
        (_, props: {childId: string}) => props.childId,
        (children, childId) => children.get(childId),
    );
};

export const getChildByProps = makeGetChildByProps();

export const makeGetChildrenByIds = () => {
    return createSelector(
        getChildren,
        (_, props: {childIds: List<string>}) => props.childIds,
        (children, childIds) => {
            return children.filter(child => childIds.contains(child.childId));
        },
    );
};

export const getChildrenByIds = makeGetChildrenByIds();

export const getInstitutionId = createSelector(getChildByProps, child => child?.institutionId);

export const getNaps = createSelector(getChildByProps, child => child?.napsSummary);

export const getChildBehaviors = createSelector(getChildByProps, child =>
    child ? child.behaviors : new BehaviorsCollection([]),
);

export const canSeeChildAccountItems = createSelector(
    getChildByProps,
    child => child && child.childBillPayersBehaviors.includes(ChildBillPayersBehaviorIds.ShowChildBalance),
);

export const getChildSplitConfiguration = createSelector(getChildByProps, child =>
    child ? child.splitConfiguration : null,
);

export const getFetchingBillPayers = createSelector(getChildByProps, child =>
    child ? child.fetchingBillPayers : null,
);

export const getChildrenFromMultipleGroups = createSelector(
    ChildrenEntity.selectors.getByMultipleGroupedId,
    (childrenGroupedByGroupId: any) => {
        return childrenGroupedByGroupId.valueSeq().toList().flatten(true);
    },
);

export const showChildPlans = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.ShowChildPlans),
);

export const showChildPurchases = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.ShowChildPurchases),
);

export const showChildBookings = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.ShowChildBookings),
);

export const showChildBilling = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.ShowChildBilling),
);

export const showLearningJourney = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.ShowLearningJourney),
);

export const showChildNotes = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.ShowChildNotes),
);

export const hasAccidentReports = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.HasAccidentReports),
);

export const showInstitutionInfo = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.ShowInstitutionInfo),
);

export const canManageChildBillPayers = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.CanManageChildBillPayers),
);

export const showCombinedBookingsAndInvoicesTab = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.ShowCombinedBookingsAndInvoicesTab),
);

export const canReportAbsence = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.CanReportChildAbsentLeave),
);

export const canSeeChildReasonForLeaving = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.CanSeeChildReasonForLeaving),
);

export const canRemoveSiblings = createSelector(getChildBehaviors, behaviors =>
    behaviors.includes(ChildBehaviors.CanRemoveSiblings),
);

export const childName = createSelector(getChildByProps, child => child && child.name.fullName);

export const childProfileImageLargeUrl = createSelector(getChildByProps, child => child && child.image.large);

export const getChildrenByInstitutionId = createSelector(
    getChildren,
    (_, props: {institutionId: string}) => props.institutionId,
    (children, institutionId) => children.filter(child => child.institutionId === institutionId),
);
